"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import EquipmentDialog, { EquipmentItem } from "./EquipmentDialog";
import { EQUIPMENT, localizeEquipment } from "@/lib/equipment-data";

export default function EquipmentGridSection() {
  const t = useTranslations("EquipmentGrid");
  const locale = useLocale() as "hr" | "en";
  const items = useMemo(() => EQUIPMENT.map((item) => localizeEquipment(item, locale)), [locale]);
  const categories = [
    { label: `${t("allLabel")} (${items.length})`, value: "all" },
    { label: `${t("machinesLabel")} (${items.length})`, value: "machines" },
  ];

  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState<EquipmentItem | null>(null);

  const filtered =
    activeCategory === "all"
      ? items
      : items.filter((item) => item.category === activeCategory);

  return (
    <section className="pb-20 pt-4">
      {/* Filter pills */}
      <div className="mb-8 flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setActiveCategory(cat.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              activeCategory === cat.value
                ? "bg-[#18181b] text-white"
                : "bg-[#F4F4F5] text-[#18181b] hover:bg-[#E4E4E7]"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelectedItem(item)}
            className="group flex flex-col overflow-hidden rounded-2xl border border-[#E4E4E7] text-left transition-shadow hover:shadow-md"
          >
            {/* Image area */}
            <div className="relative h-52 w-full bg-white">
              <Image
                src={item.image}
                alt={item.name}
                fill
                className="object-contain p-4 transition-transform duration-300 group-hover:scale-105"
              />
            </div>

            {/* Content area */}
            <div className="flex-1 bg-[#F9FAFB] px-4 pb-5 pt-4">
              <p className="mb-2 text-lg font-bold text-[#18181b]">{item.name}</p>
              <div className="flex flex-wrap gap-1.5">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-[#E4E4E7] bg-white px-2.5 py-0.5 text-xs font-medium text-[#18181b]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Dialog */}
      <EquipmentDialog item={selectedItem} onClose={() => setSelectedItem(null)} />
    </section>
  );
}
